import { useState, useEffect } from 'react';
import api from '@/lib/api';

export default function EnrichContactPanel({ candidateId, jobId }) {
  const [enrichment, setEnrichment]           = useState(null);
  const [enrichLoading, setEnrichLoading]     = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [message, setMessage]                 = useState('');
  const [msgType, setMsgType]                 = useState('');

  useEffect(() => {
    if (!candidateId) return;
    api.get(`/enrichment/candidate/${candidateId}`)
      .then(r => { if (r.data?.enriched) setEnrichment(r.data); })
      .catch(() => {});
  }, [candidateId]);

  const flash = (text, type) => {
    setMessage(text); setMsgType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleEnrich = async () => {
    setEnrichLoading(true);
    try {
      const r = await api.post(`/enrichment/candidate/${candidateId}`);
      setEnrichment(r.data.enrichment);
      flash('Contact enriched successfully', 'success');
    } catch (e) {
      flash(e.response?.data?.detail || 'Enrichment failed', 'error');
    } finally { setEnrichLoading(false); }
  };

  const handleOutreach = async () => {
    if (!jobId) {
      flash('Open this candidate from a job context to send outreach', 'error');
      return;
    }
    setOutreachLoading(true);
    try {
      const r = await api.post('/outreach/send', {
        candidate_id: candidateId,
        job_id:       jobId,
      });
      flash(r.data.message, 'success');
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to send outreach', 'error');
    } finally { setOutreachLoading(false); }
  };

  const hasVerifiedEmail = enrichment?.personal_emails?.some(e => e.verified);

  return (
    <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mt-2 mb-4"
         data-testid="enrich-contact-panel">

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Contact Enrichment
        </span>
        {enrichment?.enriched_at && (
          <span className="text-[10px] text-gray-400">
            Enriched: {new Date(enrichment.enriched_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Contact chips */}
      <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
        {enrichment ? (
          <>
            {/* Personal emails */}
            {enrichment.personal_emails?.length > 0
              ? enrichment.personal_emails.map((e) => (
                  <div key={e.email}
                       data-testid="email-chip"
                       className="flex items-center gap-1.5 px-2.5 py-1
                                  bg-white border border-gray-200 rounded-lg
                                  text-xs shadow-sm">
                    {e.verified && (
                      <span className="text-blue-500 font-bold text-sm shrink-0"
                            title="Verified — mail_status: Valid">✓</span>
                    )}
                    <span className="text-gray-400 text-[9px] uppercase tracking-wider">
                      {e.mail_status === 'Valid' ? 'Personal ✓' : 'Personal'}
                    </span>
                    <span className="text-gray-800 font-medium">{e.email}</span>
                  </div>
                ))
              : (
                <span data-testid="no-email-warning"
                      className="flex items-center gap-1 px-2.5 py-1 text-xs
                                 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg">
                  ⚠ No personal email found
                </span>
              )
            }

            {/* Personal/direct/mobile/cell phones */}
            {enrichment.personal_phones?.map((p) => (
              <div key={p.phone || p.number}
                   data-testid="phone-chip"
                   className={`flex items-center gap-1.5 px-2.5 py-1 border
                               rounded-lg text-xs shadow-sm
                               ${p.is_valid
                                 ? 'bg-white border-gray-200 text-gray-800'
                                 : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
                <span className="text-[10px] shrink-0">📱</span>
                <span className="text-gray-400 text-[9px] uppercase tracking-wider">
                  {p.type || 'Direct'}
                </span>
                <span className="font-medium">{p.phone}</span>
                {!p.is_valid && (
                  <span className="text-[9px] text-gray-400">(unverified)</span>
                )}
              </div>
            ))}

            {/* Enriched but truly empty */}
            {!enrichment.personal_emails?.length &&
             !enrichment.personal_phones?.length && (
              <span data-testid="no-contact"
                    className="text-xs text-gray-400">
                No personal contact data found for this profile
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-400 self-center">
            Not yet enriched — click Enrich Contact to fetch personal email and direct phone
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="enrich-btn"
          onClick={handleEnrich}
          disabled={enrichLoading}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium
                     rounded-lg hover:bg-blue-700 disabled:opacity-50
                     whitespace-nowrap transition-colors"
        >
          {enrichLoading
            ? '⟳ Enriching...'
            : enrichment ? '🔄 Re-Enrich' : '🔍 Enrich Contact'
          }
        </button>

        {hasVerifiedEmail && jobId && (
          <button
            data-testid="outreach-btn"
            onClick={handleOutreach}
            disabled={outreachLoading}
            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium
                       rounded-lg hover:bg-green-700 disabled:opacity-50
                       whitespace-nowrap transition-colors"
          >
            {outreachLoading ? '⟳ Sending...' : '📨 Send Outreach Email'}
          </button>
        )}
      </div>

      {/* Status message — auto-clears after 5s */}
      {message && (
        <p className={`mt-2 text-xs font-medium
                       ${msgType === 'success' ? 'text-green-700' : 'text-red-600'}`}>
          {msgType === 'success' ? '✅' : '❌'} {message}
        </p>
      )}
    </div>
  );
}
